import axios from "axios";
import { setAuthHeader } from "app/services/auth.header.service";
import { getUrlForManagerByUserId, getUrlForMembersForManager, getUrlToFetchAllManagers, getUrlToApproveActivites, getUrlToRejectActivites, getUrlToClientApproveActivites, getUrlForEligibleParents, getUrlForEligibleParentsByUser, getUrlForScopeManagers, getUrlForManagersPage } from "utils/endpointUtils/managerEndpointUtil";
import { getUrlForAllLeavePlansForManagerByDate } from "utils/endpointUtils/leavePlansEndpointUtil";
import { getUrlForAllActivitiesForManagerByDate, getUrlForAllActivitiesForMemberByDate, getUrlForOwnActivitiesForManagerByDate } from "utils/endpointUtils/activitiesEndpointUtil";
import { getUrlToCreateManager, getUrlToDeleteManager, getUrlToUpdateManager } from "utils/endpointUtils/memberEndpointUtil";
import { clientActions } from "store/client-slice";

export const getManagerByUserId = async (userId) => {
    const config = setAuthHeader();
    
    if(config === null) { 
        throw new Error("Invalid Auth Header or Token Expired");
    }

    const url = getUrlForManagerByUserId(userId);
    return await axios.get(url, config);
}

export const loadActivitiesFromBackendForManager = async (managerId, fromDate, toDate, selectedMember = 0, keyword = "") => {
    const config = setAuthHeader();
    
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = selectedMember === 0 ? getUrlForAllActivitiesForManagerByDate(managerId, fromDate, toDate) : getUrlForAllActivitiesForMemberByDate(selectedMember, fromDate, toDate);
    return await axios.get(url, config);
}

// The activities a manager logged for themselves, without their team's — what an L2 sees
// when drilling into one of their L1 managers. @since NEXT_VERSION
export const loadOwnActivitiesFromBackendForManager = async (managerId, fromDate, toDate) => {
    const config = setAuthHeader();

    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    return await axios.get(getUrlForOwnActivitiesForManagerByDate(managerId, fromDate, toDate), config);
}

export const loadMembersFromBackendForManager = async (managerId) => {
    const config = setAuthHeader();
    
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlForMembersForManager(managerId);
    return await axios.get(url, config);
}

// Cache-aware loadMembersFromBackendForManager: returns the manager_id-keyed cache
// on a hit, else fetches once and caches it. Never throws (failure resolves to []).
//
// A caller that must tell "no members" from "the request failed" — a team list is wrong
// rather than empty when a fetch fails — passes onError and is told. @since NEXT_VERSION
export const loadMembersForManagerCached = async (managerId, membersByManager = {}, dispatch, onError) => {
    const cached = membersByManager[String(managerId)];
    if (cached) return cached;

    try {
        const response = await loadMembersFromBackendForManager(managerId);
        const list = response.status === 204 || !Array.isArray(response.data) ? [] : response.data;
        if (dispatch) dispatch(clientActions.cacheMembersForManager({ managerId, data: list }));
        return list;
    } catch (error) {
        if (onError) onError(error, managerId);
        return []; // don't cache a transient failure — a later attempt should retry
    }
};

export const loadLeavePlansFromBackendForManager = async (manager, fromDate, toDate) => {
    const config = setAuthHeader();
    
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlForAllLeavePlansForManagerByDate(manager.id, fromDate, toDate);
    return await axios.get(url, config);
}

export const loadAllManagersFromBackend = async () => {
    const config = setAuthHeader();
    
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToFetchAllManagers();
    
    return await axios.get(url, config);
}
// Server-driven page fetch for the Master Data Manager grid. Returns the PageResponse envelope.
export const loadManagersPageFromBackend = async ({ page, pageSize, sortBy, sortDirection, searchQuery }) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlForManagersPage({ page, pageSize, sortBy, sortDirection, searchQuery });
    return await axios.get(url, config);
}

export const addManagerToBackend = async (data) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToCreateManager();
    return await axios.post(url, data, config);
}

export const updateManagerToBackend = async (manager_id, data) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToUpdateManager(manager_id);
    return await axios.put(url, data, config);
}

export const deleteManager = async (manager_id) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");
    
    const url = getUrlToDeleteManager(manager_id);
    return await axios.delete(url, config);
}

export const approveAllActivities = async (data) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToApproveActivites();
    return await axios.put(url, data, config);
}

export const rejectActivity = async (data) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToRejectActivites();
    return await axios.put(url, data, config);
}

// Marks approved rows as APPROVED_BY_CLIENT (frozen financial record). @since NEXT_VERSION
export const clientApproveActivities = async (data) => {
    const config = setAuthHeader();
    if(config === null) throw new Error("Invalid Auth Header or Token Expired");

    const url = getUrlToClientApproveActivites();
    return await axios.put(url, data, config);
}

export const loadEligibleParents = async (managerId) => {
    const config = setAuthHeader();
    if (config === null) throw new Error("Invalid Auth Header or Token Expired");
    const url = getUrlForEligibleParents(managerId);
    return await axios.get(url, config);
}

// Eligible parents for a manager that does not exist yet (Create Manager form).
// @since NEXT_VERSION
export const loadEligibleParentsForUser = async (userId) => {
    const config = setAuthHeader();
    if (config === null) throw new Error("Invalid Auth Header or Token Expired");
    const url = getUrlForEligibleParentsByUser(userId);
    return await axios.get(url, config);
}

export const loadScopeManagers = async (managerId) => {
    const config = setAuthHeader();
    if (config === null) throw new Error("Invalid Auth Header or Token Expired");
    const url = getUrlForScopeManagers(managerId);
    return await axios.get(url, config);
}

// Roll up the subtree across ALL the manager's records — a person holds one record per
// client and an L1 may report through any of them, so one record misses those L1s.
// @since NEXT_VERSION
export const loadScopeManagersForIds = async (managerIds, onError) => {
    const ids = [...new Set((Array.isArray(managerIds) ? managerIds : []).filter((id) => id != null))];
    if (ids.length === 0) return [];

    const lists = await Promise.all(
        ids.map((id) =>
            loadScopeManagers(id)
                .then((r) => (r.status === 204 || !Array.isArray(r.data) ? [] : r.data))
                // One id failing yields a PARTIAL subtree, which reads like a complete one;
                // onError lets the caller say so. @since NEXT_VERSION
                .catch((error) => {
                    if (onError) onError(error, id);
                    return [];
                })
        )
    );

    const seen = new Set();
    return lists.flat().filter((m) => {
        if (m?.id == null || seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
    });
}
